import { useQuery } from '@apollo/client';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  IconButton,
  LinearProgress,
  List,
  ListSubheader,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { getNotifications, Notification } from 'vendor-marketplace-shared-library';

import useAlert from '../../hooks/useAlert';
import NotificationService from '../../services/notification.service';
import NotificationRow from './NotificationRow';

type NotificationsMenuProps = {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  handleOpenNotificationsPopover: (event: React.MouseEvent<HTMLElement>) => void;
  showNotificationsBadge: boolean;
};

export default function NotificationsMenu(props: NotificationsMenuProps) {
  const { setAlert } = useAlert();
  const [allLoaded, setAllLoaded] = useState(false);
  const { data, loading, error, refetch, fetchMore } = useQuery(getNotifications, {
    notifyOnNetworkStatusChange: true,
    variables: {
      offset: 0,
    },
    onError: err => {
      setAlert({
        message: err.message || 'Could not fetch notifications',
      });
    },
  });

  const loadMore = async () => {
    await fetchMore({
      variables: {
        offset: data?.getNotifications.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        if (fetchMoreResult.getNotifications.length < 10) {
          setAllLoaded(true);
          return prev;
        }
        return {
          getNotifications: [...prev.getNotifications, ...fetchMoreResult.getNotifications],
        };
      },
    });
  };

  const updateNotificationsAsViewed = async (notificationIds: string[]) => {
    try {
      if (!notificationIds.length) return;
      await NotificationService.markNotificationsAsViewed(notificationIds);
      await refetch();
    } catch (err: any) {
      setAlert({
        message: err.response?.data?.message || 'Could not update notifications',
      });
    }
  };

  const markAllNotificationsAsRead = async () => {
    const notificationIds = data?.getNotifications.map((n: Notification) => n.id);
    await updateNotificationsAsViewed(notificationIds);
  };

  return (
    <>
      <Tooltip title='Notifications'>
        <IconButton onClick={props.handleOpenNotificationsPopover} sx={{ p: 2 }}>
          <Badge color={'error'} variant={'dot'} invisible={!props.showNotificationsBadge}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        slotProps={{
          paper: { sx: Styles.popoverSlotStyles },
        }}
        id={'notifications-menu'}
        open={Boolean(props.anchorEl)}
        anchorEl={props.anchorEl}
        onClose={props.handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ maxHeight: '85%' }}>
          <List
            subheader={
              data?.getNotifications.length > 0 && (
                <ListSubheader>
                  <Typography
                    sx={Styles.markAllAsReadText}
                    onClick={markAllNotificationsAsRead}
                    variant={'body2'}
                  >
                    Mark All as Read
                  </Typography>
                </ListSubheader>
              )
            }
          >
            {data?.getNotifications.length > 0 ? (
              data?.getNotifications.map((n: Notification) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onMarkAsRead={() => updateNotificationsAsViewed([n.id])}
                  handleClose={props.handleClose}
                />
              ))
            ) : (
              <Box padding={2}>
                <Typography>No notifications yet</Typography>
              </Box>
            )}
          </List>
          {!allLoaded && (
            <Typography
              onClick={loadMore}
              variant={'body2'}
              color={'text.secondary'}
              sx={Styles.loadMoreText}
            >
              Load More
            </Typography>
          )}
          {loading && <LinearProgress color={'info'} />}
        </Box>
      </Popover>
    </>
  );
}

const Styles = {
  popoverSlotStyles: { maxWidth: '50%' },
  markAllAsReadText: {
    mt: 2,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
    textAlign: 'right',
  },
  loadMoreText: {
    textAlign: 'center',
    m: 1,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
};
